import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import { getUser } from '../utils/auth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ShoppingCartIcon, ShoppingBagIcon, CurrencyDollarIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

export default function ProfilePage() {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState({ sales: [], purchases: [], totalSales: 0, totalPurchases: 0, ebookCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    Promise.all([
      api.get('/api/seller/orders').catch(() => ({ data: [] })),
      api.get('/api/orders/my').catch(() => ({ data: [] })),
      api.get('/api/seller/ebooks').catch(() => ({ data: [] }))
    ])
      .then(([salesRes, purchasesRes, ebooksRes]) => {
        const sales = salesRes.data || [];
        const purchases = purchasesRes.data || [];
        const ebooks = ebooksRes.data || [];

        const totalSales = sales.filter(o => o.status === 'paid').reduce((sum, o) => sum + Number(o.amount || 0), 0);
        const totalPurchases = purchases.filter(o => o.status === 'paid').reduce((sum, o) => sum + Number(o.amount || 0), 0);

        setStats({ sales, purchases, totalSales, totalPurchases, ebookCount: ebooks.length });
      })
      .catch(() => toast.error('Gagal memuat statistik'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <Navbar title="Profil" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400">Memuat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar title="Profil" />
      
      <div className="p-4 max-w-4xl mx-auto w-full space-y-4">
        {/* User Info */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-wa-green flex items-center justify-center text-white text-2xl font-semibold">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{user.name}</h2>
              <p className="text-sm text-gray-500">{user.email}</p>
              {user.tenant_slug && (
                <p className="text-xs text-wa-dark font-medium mt-1">Toko: {user.tenant_slug}</p>
              )}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <Link to="/affiliate" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
              <CurrencyDollarIcon className="w-4 h-4" />
              Program Affiliate
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 text-wa-dark mb-2">
              <BookOpenIcon className="w-5 h-5" />
              <span className="text-xs font-medium">Ebook Saya</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.ebookCount}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <ShoppingBagIcon className="w-5 h-5" />
              <span className="text-xs font-medium">Penjualan</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.sales.filter(o => o.status === 'paid').length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <ShoppingCartIcon className="w-5 h-5" />
              <span className="text-xs font-medium">Pembelian</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.purchases.filter(o => o.status === 'paid').length}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center gap-2 text-yellow-600 mb-2">
              <CurrencyDollarIcon className="w-5 h-5" />
              <span className="text-xs font-medium">Pendapatan</span>
            </div>
            <p className="text-lg font-bold text-gray-800">Rp {stats.totalSales.toLocaleString('id-ID')}</p>
          </div>
        </div>

        {/* Recent Sales */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <ShoppingBagIcon className="w-5 h-5 text-wa-dark" />
            Penjualan Terakhir
          </h3>
          {stats.sales.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">Belum ada penjualan</p>
          ) : (
            <div className="space-y-2">
              {stats.sales.slice(0, 5).map(sale => (
                <div key={sale.id} className="flex justify-between items-center text-sm py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-800">{sale.ebook_title || 'Ebook'}</p>
                    <p className="text-xs text-gray-500">{sale.buyer_name || sale.buyer_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-wa-dark">Rp {Number(sale.amount || 0).toLocaleString('id-ID')}</p>
                    <span className={`text-xs ${sale.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {sale.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Purchases */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <ShoppingCartIcon className="w-5 h-5 text-blue-600" />
            Pembelian Terakhir
          </h3>
          {stats.purchases.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">Belum ada pembelian</p>
          ) : (
            <div className="space-y-2">
              {stats.purchases.slice(0, 5).map(purchase => (
                <div key={purchase.id} className="flex justify-between items-center text-sm py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-800">{purchase.ebook_title || 'Ebook'}</p>
                    <p className="text-xs text-gray-500">{purchase.seller_name || 'Penjual'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-700">Rp {Number(purchase.amount || 0).toLocaleString('id-ID')}</p>
                    <span className={`text-xs ${purchase.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {purchase.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
